import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon, ArrowUpRightIcon, SearchIcon, BuildingIcon, CreditCardIcon, HistoryIcon, ArrowLeftIcon, InfoIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
import ExportMenu from '../../components/ui/ExportMenu';
// Mock individual merchant data
const individualMerchantsData = [{
  id: 'IM001',
  name: 'Downtown Electronics',
  businessType: 'Electronics',
  taxId: 'TX-1234567',
  status: 'active',
  superMerchant: 'Global Retail Group',
  superMerchantId: 'SM001',
  transactionVolume: 125000,
  lastActivity: '2023-07-16T10:20:00Z',
  contactPerson: 'Michael Lee',
  email: 'michael@downtownelec.com',
  phone: '+1 (555) 123-4567',
  address: '123 Main St, Downtown, NY 10001',
  paymentMethods: ['POS', 'QR', 'Online']
}, {
  id: 'IM002',
  name: 'Fashion Forward',
  businessType: 'Apparel',
  taxId: 'TX-7654321',
  status: 'inactive',
  superMerchant: 'Global Retail Group',
  superMerchantId: 'SM001',
  transactionVolume: 85000,
  lastActivity: '2023-07-10T14:15:00Z',
  contactPerson: 'Sophia Martinez',
  email: 'sophia@fashionforward.com',
  phone: '+1 (555) 987-6543',
  address: '456 Fashion Ave, Midtown, NY 10018',
  paymentMethods: ['POS', 'Online']
}, {
  id: 'IM003',
  name: 'Gourmet Coffee Shop',
  businessType: 'Food & Beverage',
  taxId: 'TX-9876543',
  status: 'active',
  superMerchant: 'Fast Food Alliance',
  superMerchantId: 'SM002',
  transactionVolume: 75000,
  lastActivity: '2023-07-16T08:20:00Z',
  contactPerson: 'James Wilson',
  email: 'james@gourmetcoffee.com',
  phone: '+1 (555) 456-7890',
  address: '789 Café Lane, SoHo, NY 10013',
  paymentMethods: ['POS', 'QR']
}, {
  id: 'IM004',
  name: 'Premium Phones',
  businessType: 'Electronics',
  taxId: 'TX-5432198',
  status: 'active',
  superMerchant: 'Tech Distribution Network',
  superMerchantId: 'SM003',
  transactionVolume: 95000,
  lastActivity: '2023-07-05T10:15:00Z',
  contactPerson: 'Emily Brown',
  email: 'emily@premiumphones.com',
  phone: '+1 (555) 234-5678',
  address: '101 Tech Plaza, Financial District, NY 10005',
  paymentMethods: ['POS', 'QR', 'Online']
}, {
  id: 'IM005',
  name: 'Seaside Resort',
  businessType: 'Hospitality',
  taxId: 'TX-8765432',
  status: 'active',
  superMerchant: 'Hospitality Group International',
  superMerchantId: 'SM004',
  transactionVolume: 120000,
  lastActivity: '2023-07-15T12:20:00Z',
  contactPerson: 'Daniel Smith',
  email: 'daniel@seasideresort.com',
  phone: '+1 (555) 876-5432',
  address: '202 Oceanview Drive, Hamptons, NY 11937',
  paymentMethods: ['POS', 'QR', 'Online', 'NFC']
}];
// Mock transaction data
const transactionsData = {
  IM001: [{
    id: 'T1001',
    date: '2023-07-16T10:15:00Z',
    amount: 1250.0,
    paymentMethod: 'Credit Card',
    status: 'completed',
    customer: 'John Doe',
    reference: 'REF-12345'
  }, {
    id: 'T1002',
    date: '2023-07-15T14:30:00Z',
    amount: 895.5,
    paymentMethod: 'QR',
    status: 'completed',
    customer: 'Jane Smith',
    reference: 'REF-12346'
  }, {
    id: 'T1003',
    date: '2023-07-15T09:45:00Z',
    amount: 2450.0,
    paymentMethod: 'Online',
    status: 'completed',
    customer: 'Robert Johnson',
    reference: 'REF-12347'
  }, {
    id: 'T1004',
    date: '2023-07-14T16:20:00Z',
    amount: 750.25,
    paymentMethod: 'POS',
    status: 'refunded',
    customer: 'Sarah Williams',
    reference: 'REF-12348'
  }, {
    id: 'T1005',
    date: '2023-07-13T11:10:00Z',
    amount: 1875.0,
    paymentMethod: 'Credit Card',
    status: 'failed',
    customer: 'Michael Brown',
    reference: 'REF-12349'
  }],
  IM003: [{
    id: 'T2001',
    date: '2023-07-16T08:15:00Z',
    amount: 45.75,
    paymentMethod: 'QR',
    status: 'completed',
    customer: 'Emily Davis',
    reference: 'REF-23456'
  }, {
    id: 'T2002',
    date: '2023-07-16T07:30:00Z',
    amount: 32.5,
    paymentMethod: 'POS',
    status: 'completed',
    customer: 'David Wilson',
    reference: 'REF-23457'
  }, {
    id: 'T2003',
    date: '2023-07-15T16:45:00Z',
    amount: 78.25,
    paymentMethod: 'QR',
    status: 'completed',
    customer: 'Linda Miller',
    reference: 'REF-23458'
  }]
};
// Mock super merchants for dropdown
const superMerchantsOptions = [{
  id: 'SM001',
  name: 'Global Retail Group'
}, {
  id: 'SM002',
  name: 'Fast Food Alliance'
}, {
  id: 'SM003',
  name: 'Tech Distribution Network'
}, {
  id: 'SM004',
  name: 'Hospitality Group International'
}, {
  id: 'SM005',
  name: 'Urban Transport Consortium'
}];
interface IndividualMerchant {
  id: string;
  name: string;
  businessType: string;
  taxId: string;
  status: string;
  superMerchant: string;
  superMerchantId: string;
  transactionVolume: number;
  lastActivity: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  paymentMethods: string[];
}
interface Transaction {
  id: string;
  date: string;
  amount: number;
  paymentMethod: string;
  status: string;
  customer: string;
  reference: string;
}
const IndividualMerchant: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedMerchantId = queryParams.get('id');
  const [merchants, setMerchants] = useState<IndividualMerchant[]>(individualMerchantsData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<IndividualMerchant | null>(selectedMerchantId ? individualMerchantsData.find(m => m.id === selectedMerchantId) || null : null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingTransactions, setViewingTransactions] = useState(!!selectedMerchantId);
  // Form state for add/edit modal
  const [formData, setFormData] = useState<Partial<IndividualMerchant>>({
    name: '',
    businessType: '',
    taxId: '',
    status: 'active',
    superMerchantId: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    paymentMethods: []
  });
  const handleAddMerchant = () => {
    setIsEditing(false);
    setFormData({
      name: '',
      businessType: '',
      taxId: '',
      status: 'active',
      superMerchantId: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      paymentMethods: []
    });
    setShowAddEditModal(true);
  };
  const handleEditMerchant = (merchant: IndividualMerchant) => {
    setIsEditing(true);
    setFormData({
      ...merchant
    });
    setShowAddEditModal(true);
  };
  const handleDeleteMerchant = (merchant: IndividualMerchant) => {
    setSelectedMerchant(merchant);
    setShowDeleteModal(true);
  };
  const handleViewMerchant = (merchant: IndividualMerchant) => {
    setSelectedMerchant(merchant);
    setShowViewModal(true);
  };
  const handleViewTransactions = (merchant: IndividualMerchant) => {
    setSelectedMerchant(merchant);
    setViewingTransactions(true);
    // Update URL to include merchant ID
    navigate(`/individual-merchant?id=${merchant.id}`);
  };
  const handleBackToMerchantList = () => {
    setViewingTransactions(false);
    setSelectedMerchant(null);
    // Remove query params from URL
    navigate('/individual-merchant');
  };
  const confirmDelete = () => {
    if (selectedMerchant) {
      setMerchants(merchants.filter(m => m.id !== selectedMerchant.id));
      setShowDeleteModal(false);
      setSelectedMerchant(null);
      if (viewingTransactions) {
        setViewingTransactions(false);
        navigate('/individual-merchant');
      }
    }
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePaymentMethodChange = (method: string) => {
    setFormData(prev => {
      const currentMethods = prev.paymentMethods || [];
      if (currentMethods.includes(method)) {
        return {
          ...prev,
          paymentMethods: currentMethods.filter(m => m !== method)
        };
      } else {
        return {
          ...prev,
          paymentMethods: [...currentMethods, method]
        };
      }
    });
  };
  const handleSubmitForm = () => {
    if (isEditing && selectedMerchant) {
      // Update existing merchant
      const updatedMerchants = merchants.map(m => m.id === selectedMerchant.id ? {
        ...m,
        ...formData,
        // Keep these fields as they are
        transactionVolume: m.transactionVolume,
        lastActivity: m.lastActivity,
        // Update superMerchant name based on ID
        superMerchant: superMerchantsOptions.find(sm => sm.id === formData.superMerchantId)?.name || m.superMerchant
      } : m);
      setMerchants(updatedMerchants);
      if (viewingTransactions) {
        setSelectedMerchant(updatedMerchants.find(m => m.id === selectedMerchant.id) || null);
      }
    } else {
      // Add new merchant
      const superMerchantInfo = superMerchantsOptions.find(sm => sm.id === formData.superMerchantId);
      const newMerchant: IndividualMerchant = {
        id: `IM${String(merchants.length + 1).padStart(3, '0')}`,
        name: formData.name || '',
        businessType: formData.businessType || '',
        taxId: formData.taxId || '',
        status: formData.status || 'active',
        superMerchantId: formData.superMerchantId || '',
        superMerchant: superMerchantInfo?.name || 'Unassigned',
        transactionVolume: 0,
        lastActivity: new Date().toISOString(),
        contactPerson: formData.contactPerson || '',
        email: formData.email || '',
        phone: formData.phone || '',
        address: formData.address || '',
        paymentMethods: formData.paymentMethods || []
      };
      setMerchants([...merchants, newMerchant]);
    }
    setShowAddEditModal(false);
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting ${viewingTransactions ? 'transactions' : 'merchants'} as ${format}`);
    // Implementation for export functionality would go here
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-1446" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-1447" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-1448" />;
      case 'completed':
        return <StatusBadge status="success" text="Completed" data-id="element-1449" />;
      case 'failed':
        return <StatusBadge status="danger" text="Failed" data-id="element-1450" />;
      case 'refunded':
        return <StatusBadge status="warning" text="Refunded" data-id="element-1451" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-1452" />;
    }
  };
  const merchantColumns = [{
    header: 'Merchant',
    accessor: (merchant: IndividualMerchant) => <div data-id="element-1453">
          <div className="font-medium" data-id="element-1454">{merchant.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1455">
            {merchant.id}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Business Type',
    accessor: 'businessType',
    sortable: true
  }, {
    header: 'Super Merchant',
    accessor: 'superMerchant',
    sortable: true
  }, {
    header: 'Status',
    accessor: (merchant: IndividualMerchant) => getStatusBadge(merchant.status),
    sortable: false
  }, {
    header: 'Transaction Volume',
    accessor: (merchant: IndividualMerchant) => formatCurrency(merchant.transactionVolume),
    sortable: true
  }, {
    header: 'Last Activity',
    accessor: (merchant: IndividualMerchant) => formatDateTime(merchant.lastActivity),
    sortable: true
  }];
  const transactionColumns = [{
    header: 'Transaction ID',
    accessor: 'id',
    sortable: true
  }, {
    header: 'Date & Time',
    accessor: (transaction: Transaction) => formatDateTime(transaction.date),
    sortable: true
  }, {
    header: 'Amount',
    accessor: (transaction: Transaction) => formatCurrency(transaction.amount),
    sortable: true
  }, {
    header: 'Payment Method',
    accessor: 'paymentMethod',
    sortable: true
  }, {
    header: 'Status',
    accessor: (transaction: Transaction) => getStatusBadge(transaction.status),
    sortable: false
  }, {
    header: 'Customer',
    accessor: 'customer',
    sortable: true
  }, {
    header: 'Reference',
    accessor: 'reference',
    sortable: true
  }];
  const filteredMerchants = merchants.filter(merchant => merchant.name.toLowerCase().includes(searchQuery.toLowerCase()) || merchant.id.toLowerCase().includes(searchQuery.toLowerCase()) || merchant.businessType.toLowerCase().includes(searchQuery.toLowerCase()) || merchant.superMerchant.toLowerCase().includes(searchQuery.toLowerCase()));
  const currentTransactions = selectedMerchant ? transactionsData[selectedMerchant.id] || [] : [];
  return <div className="space-y-6" data-id="element-1456">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1457">
        {viewingTransactions && selectedMerchant ? <>
            <div data-id="element-1458">
              <button onClick={handleBackToMerchantList} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-1459">
                <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1460" />
                Back to Individual Merchants
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-1461">
                {selectedMerchant.name} Transactions
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1462">
                View and manage transactions for {selectedMerchant.name} (
                {selectedMerchant.id})
              </p>
            </div>
            <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-1463">
              <button onClick={() => {}} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1464">
                <div className="h-4 w-4 mr-2" data-id="element-1465" />
                Refresh
              </button>
              <ExportMenu onExport={handleExport} data-id="element-1466" />
            </div>
          </> : <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1467">
              Individual Merchant Management
            </h1>
            <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-1468">
              <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1469">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-1470" />
                Filters
              </button>
              <ExportMenu onExport={handleExport} data-id="element-1471" />
              <button onClick={handleAddMerchant} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1472">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-1473" />
                Add Individual Merchant
              </button>
            </div>
          </>}
      </div>
      {/* Dashboard Cards - Only show when viewing transactions */}
      {viewingTransactions && selectedMerchant && <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1474">
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800" data-id="element-1475">
            <div className="flex items-center" data-id="element-1476">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-800 mr-4" data-id="element-1477">
                <CreditCardIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-1478" />
              </div>
              <div data-id="element-1479">
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400" data-id="element-1480">
                  Total Transactions
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-1481">
                  {currentTransactions.length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800" data-id="element-1482">
            <div className="flex items-center" data-id="element-1483">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-800 mr-4" data-id="element-1484">
                <ArrowUpRightIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-1485" />
              </div>
              <div data-id="element-1486">
                <p className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-1487">
                  Transaction Volume
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-1488">
                  {formatCurrency(currentTransactions.reduce((sum, t) => sum + t.amount, 0))}
                </p>
              </div>
            </div>
          </Card>
          <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800" data-id="element-1489">
            <div className="flex items-center" data-id="element-1490">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-800 mr-4" data-id="element-1491">
                <HistoryIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-1492" />
              </div>
              <div data-id="element-1493">
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-1494">
                  Last Transaction
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-1495">
                  {currentTransactions.length > 0 ? formatDateTime(currentTransactions[0].date).split(',')[0] : 'N/A'}
                </p>
              </div>
            </div>
          </Card>
        </div>}
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-1496">
        <div className="relative flex-grow" data-id="element-1497">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1498">
            <SearchIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1499" />
          </div>
          <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full pl-10 p-2.5" placeholder={viewingTransactions ? 'Search transactions...' : 'Search by name, ID, or business type...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1500" />
        </div>
      </div>
      {showFilters && !viewingTransactions && <Card data-id="element-1501">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1502">
            <div data-id="element-1503">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1504">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-1505">
                <option value="" data-id="element-1506">All Statuses</option>
                <option value="active" data-id="element-1507">Active</option>
                <option value="inactive" data-id="element-1508">Inactive</option>
                <option value="pending" data-id="element-1509">Pending</option>
              </select>
            </div>
            <div data-id="element-1510">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1511">
                Business Type
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-1512">
                <option value="" data-id="element-1513">All Types</option>
                <option value="Electronics" data-id="element-1514">Electronics</option>
                <option value="Apparel" data-id="element-1515">Apparel</option>
                <option value="Food & Beverage" data-id="element-1516">Food & Beverage</option>
                <option value="Hospitality" data-id="element-1517">Hospitality</option>
              </select>
            </div>
            <div data-id="element-1518">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1519">
                Super Merchant
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-1520">
                <option value="" data-id="element-1521">All Super Merchants</option>
                {superMerchantsOptions.map(sm => <option key={sm.id} value={sm.id} data-id="element-1522">
                    {sm.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-1523">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1524">
              Reset
            </button>
            <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1525">
              Apply Filters
            </button>
          </div>
        </Card>}
      <Card data-id="element-1526">
        {viewingTransactions && selectedMerchant ? <Table columns={transactionColumns} data={currentTransactions} keyField="id" actions={transaction => <div className="flex justify-end space-x-2" data-id="element-1528">
                <button className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View Details" data-id="element-1529">
                  <EyeIcon className="h-5 w-5" data-id="element-1530" />
                </button>
              </div>} data-id="element-1527" /> : <Table columns={merchantColumns} data={filteredMerchants} keyField="id" actions={merchant => <div className="flex justify-end space-x-2" data-id="element-1532">
                <button onClick={() => handleViewMerchant(merchant)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View" data-id="element-1533">
                  <EyeIcon className="h-5 w-5" data-id="element-1534" />
                </button>
                <button onClick={() => handleViewTransactions(merchant)} className="p-1 rounded-full text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30" title="View Transactions" data-id="element-1535">
                  <HistoryIcon className="h-5 w-5" data-id="element-1536" />
                </button>
                <button onClick={() => handleEditMerchant(merchant)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-1537">
                  <EditIcon className="h-5 w-5" data-id="element-1538" />
                </button>
                <button onClick={() => handleDeleteMerchant(merchant)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-1539">
                  <TrashIcon className="h-5 w-5" data-id="element-1540" />
                </button>
              </div>} data-id="element-1531" />}
      </Card>
      {/* View Merchant Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Individual Merchant Details" size="lg" data-id="element-1541">
        {selectedMerchant && <div className="space-y-6" data-id="element-1542">
            <div className="flex items-center" data-id="element-1543">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-800 mr-4" data-id="element-1544">
                <BuildingIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-1545" />
              </div>
              <div data-id="element-1546">
                <h3 className="text-lg font-medium" data-id="element-1547">{selectedMerchant.name}</h3>
                <div className="flex items-center mt-1" data-id="element-1548">
                  {getStatusBadge(selectedMerchant.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-1549">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1550">
                    {selectedMerchant.businessType}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1551">
              <div data-id="element-1552">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1553">
                  Merchant Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-1554">
                  <div className="flex justify-between" data-id="element-1555">
                    <span className="text-sm font-medium" data-id="element-1556">ID:</span>
                    <span className="text-sm" data-id="element-1557">{selectedMerchant.id}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1558">
                    <span className="text-sm font-medium" data-id="element-1559">Tax ID:</span>
                    <span className="text-sm" data-id="element-1560">{selectedMerchant.taxId}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1561">
                    <span className="text-sm font-medium" data-id="element-1562">Super Merchant:</span>
                    <span className="text-sm" data-id="element-1563">
                      {selectedMerchant.superMerchant}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1564">
                    <span className="text-sm font-medium" data-id="element-1565">
                      Transaction Volume:
                    </span>
                    <span className="text-sm" data-id="element-1566">
                      {formatCurrency(selectedMerchant.transactionVolume)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1567">
                    <span className="text-sm font-medium" data-id="element-1568">Last Activity:</span>
                    <span className="text-sm" data-id="element-1569">
                      {formatDateTime(selectedMerchant.lastActivity)}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-1570">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1571">
                  Contact Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-1572">
                  <div className="flex justify-between" data-id="element-1573">
                    <span className="text-sm font-medium" data-id="element-1574">Contact Person:</span>
                    <span className="text-sm" data-id="element-1575">
                      {selectedMerchant.contactPerson}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1576">
                    <span className="text-sm font-medium" data-id="element-1577">Email:</span>
                    <span className="text-sm" data-id="element-1578">{selectedMerchant.email}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1579">
                    <span className="text-sm font-medium" data-id="element-1580">Phone:</span>
                    <span className="text-sm" data-id="element-1581">{selectedMerchant.phone}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1582">
                    <span className="text-sm font-medium" data-id="element-1583">Address:</span>
                    <span className="text-sm" data-id="element-1584">{selectedMerchant.address}</span>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-1585">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1586">
                Payment Methods
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1587">
                {selectedMerchant.paymentMethods.map(method => <span key={method} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium" data-id="element-1588">
                    {method}
                  </span>)}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-1589">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-1590">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1591">
                <button onClick={() => {
              setShowViewModal(false);
              handleViewTransactions(selectedMerchant);
            }} className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-1592">
                  View Transactions
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-1593">
                  Generate QR Code
                </button>
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-1594">
                  View Settlement
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Add/Edit Merchant Modal */}
      <Modal isOpen={showAddEditModal} onClose={() => setShowAddEditModal(false)} title={isEditing ? 'Edit Individual Merchant' : 'Add Individual Merchant'} size="lg" footer={<>
            <button onClick={() => setShowAddEditModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1596">
              Cancel
            </button>
            <button onClick={handleSubmitForm} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1597">
              {isEditing ? 'Update' : 'Add'} Merchant
            </button>
          </>} data-id="element-1595">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1598">
          <div className="md:col-span-2" data-id="element-1599">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1600">
              Merchant Name <span className="text-red-500" data-id="element-1601">*</span>
            </label>
            <input type="text" name="name" value={formData.name || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter merchant name" required data-id="element-1602" />
          </div>
          <div data-id="element-1603">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1604">
              Business Type <span className="text-red-500" data-id="element-1605">*</span>
            </label>
            <select name="businessType" value={formData.businessType || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-1606">
              <option value="" data-id="element-1607">Select Business Type</option>
              <option value="Electronics" data-id="element-1608">Electronics</option>
              <option value="Apparel" data-id="element-1609">Apparel</option>
              <option value="Food & Beverage" data-id="element-1610">Food & Beverage</option>
              <option value="Hospitality" data-id="element-1611">Hospitality</option>
              <option value="Healthcare" data-id="element-1612">Healthcare</option>
              <option value="Education" data-id="element-1613">Education</option>
              <option value="Entertainment" data-id="element-1614">Entertainment</option>
            </select>
          </div>
          <div data-id="element-1615">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1616">
              Tax ID <span className="text-red-500" data-id="element-1617">*</span>
            </label>
            <input type="text" name="taxId" value={formData.taxId || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter tax ID" required data-id="element-1618" />
          </div>
          <div data-id="element-1619">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1620">
              Super Merchant
            </label>
            <select name="superMerchantId" value={formData.superMerchantId || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-1621">
              <option value="" data-id="element-1622">Select Super Merchant (Optional)</option>
              {superMerchantsOptions.map(sm => <option key={sm.id} value={sm.id} data-id="element-1623">
                  {sm.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-1624">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1625">
              Contact Person <span className="text-red-500" data-id="element-1626">*</span>
            </label>
            <input type="text" name="contactPerson" value={formData.contactPerson || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter contact person name" required data-id="element-1627" />
          </div>
          <div data-id="element-1628">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1629">
              Status
            </label>
            <select name="status" value={formData.status || 'active'} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-1630">
              <option value="active" data-id="element-1631">Active</option>
              <option value="inactive" data-id="element-1632">Inactive</option>
              <option value="pending" data-id="element-1633">Pending</option>
            </select>
          </div>
          <div data-id="element-1634">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1635">
              Email <span className="text-red-500" data-id="element-1636">*</span>
            </label>
            <input type="email" name="email" value={formData.email || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter email address" required data-id="element-1637" />
          </div>
          <div data-id="element-1638">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1639">
              Phone <span className="text-red-500" data-id="element-1640">*</span>
            </label>
            <input type="tel" name="phone" value={formData.phone || ''} onChange={handleFormChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter phone number" required data-id="element-1641" />
          </div>
          <div className="md:col-span-2" data-id="element-1642">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1643">
              Address <span className="text-red-500" data-id="element-1644">*</span>
            </label>
            <textarea name="address" value={formData.address || ''} onChange={handleFormChange} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter merchant address" required data-id="element-1645" />
          </div>
          <div className="md:col-span-2" data-id="element-1646">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1647">
              Allowed Payment Methods
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-1648">
              {['POS', 'QR', 'Online', 'NFC'].map(method => <div key={method} className="flex items-center" data-id="element-1649">
                  <input type="checkbox" id={`payment-${method}`} checked={(formData.paymentMethods || []).includes(method)} onChange={() => handlePaymentMethodChange(method)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1650" />
                  <label htmlFor={`payment-${method}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-1651">
                    {method}
                  </label>
                </div>)}
            </div>
          </div>
          <div className="md:col-span-2 flex items-start mt-2" data-id="element-1652">
            <InfoIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1653" />
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1654">
              After adding the merchant, you can configure transaction limits,
              settlement rules, and generate QR codes.
            </p>
          </div>
        </div>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1656">
              Cancel
            </button>
            <button onClick={confirmDelete} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-1657">
              Delete
            </button>
          </>} data-id="element-1655">
        <div className="space-y-4" data-id="element-1658">
          <p className="text-gray-700 dark:text-gray-300" data-id="element-1659">
            Are you sure you want to delete the merchant "
            {selectedMerchant?.name}"? This action cannot be undone.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-300" data-id="element-1660">
            <p className="font-medium" data-id="element-1661">
              Warning: All merchant data will be deleted
            </p>
            <p className="mt-1" data-id="element-1662">
              Deleting this merchant will remove all associated transaction
              history, payment methods, and configurations.
            </p>
          </div>
        </div>
      </Modal>
    </div>;
};
export default IndividualMerchant;